import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {AuthRESTService} from '../../../services/rest/auth-rest.service';
import Commons from '../../commons';
import { ToastrService } from 'ngx-toastr';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { MetaPropertySchema } from '../../../../models/metaPropertySchema';
import { Observable, forkJoin, of } from "rxjs";
import {AuthControllerService} from "../../../../services/auth-controller/auth-controller.service";
import { SharedModule } from 'app/shared.module';
import { AppWidgetModule } from 'app/components/widgets/widget.module';


@Component({
    standalone: true,
    imports: [SharedModule, AppWidgetModule],
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.css']
})
export class UserPage extends Commons implements OnInit {
	user;
	isModifySelf: boolean;
	exceptPropNames = ['password'];
	currentUserIsAdmin: boolean;
	currentUserIsUserAdmin: boolean;
	roles = {};
	initRoles = {};
	availableRoles: string [];
	alertVerify: boolean = false;
	alertVerifyConfirm: boolean = false;
	alertVerifySelectedProps: Array<any> = [];
	focusedField: any = null;

	constructor(private route: ActivatedRoute, private rest: AuthRESTService, toastr: ToastrService, translate: TranslateService,
		private authService: AuthControllerService) {
        super(toastr, translate)
    }

    ngOnInit(): void {
		this.bindRoles = this.bindRoles.bind(this);
		this.authService.subscribeForTokenChange(this.bindRoles);
		this.bindRoles();
		this.initializeUserData();

		this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
			this.initializeUserData();
		});
	}

	ngOnDestroy(): void {
		this.authService.unsubscribe(this.bindRoles);
	}

    initializeUserData() {
		this.route.params.subscribe(params => {
			this.isModifySelf = !params['id'];
			let fileUploadUrlPrefix = this.isModifySelf ? 'user/bdata/' : 'manager/user/bdata/';
			let fileSaveUrlPrefix = this.isModifySelf ? 'user/bdata/' : 'manager/user/' +  params['id'] + '/bdata/';
			let getUser = this.isModifySelf ? this.rest.get('user/?lang=' + localStorage.getItem('userLang') || this.translate.getDefaultLang()) : this.rest.get(`manager/user/${params['id']}`);
			let getAvailableRoles = (this.currentUserIsAdmin || this.currentUserIsUserAdmin) ? this.rest.get('roles/') : of(null);
			let getProps = this.rest.get('cas/configuration/property');
			forkJoin([getUser, getProps, getAvailableRoles]).subscribe(results => {
				const userProps = results[0].properties;
				let props = results[1];
				delete results[0].properties;
				this.user = results[0];
				this.availableRoles = results[2];
				if (this.availableRoles) {
					this.availableRoles.forEach((r) => {
						this.roles[r] = results[0].roles.some((userRole) => {
							return userRole === r;
						});
						this.initRoles[r] = this.roles[r];
					});
				}
				userProps.push(Object.assign({}, this.user.name, { name: "name", value: { value: this.user.name.value } }));
				this.user.properties = props.map((prop, idx) => {
					let currentProp, extraProps = { deleteUrl: null, saveUrl: null, imageUrls: []};
					const display = prop.display ? JSON.parse(prop.display) : null;
					currentProp = Object.assign({ value: { value: null } }, prop);
					userProps.some((userProp) => {
						if (userProp.id === prop.id) {
							if ((display && display.multiple) || currentProp.binary) {
								currentProp.value = userProp.values;
							} else {
								currentProp.value = Array.isArray(userProp.values) ? userProp.values[0] : userProp.value;
							}

							if (display && display.type && display.type === "checkbox") { // only single checkbox for now
								currentProp.value.value = Boolean(currentProp.value && currentProp.value.value === "true");
							}

							return true;
						}
					});
					if (!currentProp.usedOnForm) return false;
					if (this.exceptPropNames.some((exceptPropName) => {
						if (exceptPropName === currentProp.name) return true;
					})) { // except this one
						return false;
					}
					if (currentProp.binary) {
						extraProps.saveUrl = fileSaveUrlPrefix + currentProp.id;
						extraProps.deleteUrl = fileSaveUrlPrefix + currentProp.id;
						extraProps.imageUrls = [];
						if (currentProp.value && Array.isArray(currentProp.value)) {
							extraProps.imageUrls = currentProp.value.map((value) => {
								return fileUploadUrlPrefix + value.id;
							});
						}
					}
					return Object.assign({}, currentProp, extraProps);
				}).filter(Boolean).sort((a, b) => { return a.ordering - b.ordering }).map((prop) => {
					let result = new MetaPropertySchema();
					result.translateService = this.translate;
					const uProp = userProps.find((up) => up.name === prop.name);
					result.verified = uProp ? uProp.verified : false;
					return Object.assign(result, prop, { display: (prop.display && JSON.parse(prop.display)) || result.display });
				});
			}, err => this.errorHandler(err));
		})
	}

	onFileListChange(items) {
		this.initializeUserData();
	}

	fieldChanged(event, props) {
		if (props.verified) {
			console.log("changed");
		}
	}

	fieldFocused(event, props) {
		if (props.verified) {
			if (!this.alertVerifyConfirm) {
				this.focusedField = event.target;
				this.getInvalidatingPropertiesList(props.invalidating_properties);
				this.alertVerify = !this.alertVerify;
			}
			else {
				this.alertVerifyConfirm = false;
			}
		}
	}

	reVerify(event, props) {
		if(props.name === 'primary_email') {
			this.rest.put('email/set-verified', {user_id: String(this.user.id)}).subscribe(response => {
				props.verified = true;
				this.addSuccess('set.mail.verified');
			}, error => {
				this.errorHandler(error);
			})
		}
	}

	private bindRoles() {
		const loggedUser = this.authService.loggedUser();
		if (!loggedUser) {
			this.currentUserIsAdmin = false;
			this.currentUserIsUserAdmin = false;
			return;
		}
		this.currentUserIsAdmin = loggedUser.roles.indexOf('sysadmin') > -1;
		this.currentUserIsUserAdmin = loggedUser.roles.indexOf('user_admin') > -1;
	}

	public getInvalidatingPropertiesList( propsList: any[] ) {
		propsList.forEach( (invalPropIndex: any) => {
			let itemToAdd = this.user.properties.find( item => item.id === invalPropIndex );
			if (itemToAdd.verified) {
				this.alertVerifySelectedProps.push(itemToAdd);
				if (itemToAdd.invalidating_properties.length > 0) {
					this.getInvalidatingPropertiesList( itemToAdd.invalidating_properties )
				}
			}
		});
	}

	public verifyAlertClosed() {
		this.alertVerify = false;
		this.alertVerifySelectedProps = [];
	}

	public verifyAlertProceed() {
		this.alertVerify = !this.alertVerify;
		this.alertVerifyConfirm = true;
		this.alertVerifySelectedProps = [];
	}

    save(): void {
		this.clearNotifications();
		let result = [];
		this.user.properties.forEach((prop) => {
			if (prop.value == null || prop.value.value == null) return;
			if (!this.currentUserIsAdmin && !this.currentUserIsUserAdmin && prop.used_for_login) return;
			if (prop.binary) return;
			let propValue = Object.assign({}, prop.value);
			if (prop.display && prop.display.type && prop.display.type === "checkbox") {
				propValue.value = prop.value.value ? "true" : "false";
			}
			result.push({
				id: prop.id,
				values: [propValue]
			});
		});
		let post = this.isModifySelf ? this.rest.post('user/modifyBatch', result, { observe: 'response', responseType: 'text' }) : this.rest.post('manager/modifyBatch/' + this.user.id, result, { observe: 'response', responseType: 'text' });
        post.subscribe((response) => {
				this.addSuccess('general.saved');
            }, err => {
				console.log(err);
				this.errorHandler(err);
			}
		)
		if (!this.currentUserIsAdmin && !this.currentUserIsUserAdmin) return;
		var roleRequests = [];
		Object.keys(this.roles).filter((r) => {
			return this.initRoles[r] !== this.roles[r];
		}).forEach((r) => {
			if (this.roles[r]) {
				roleRequests.push(this.rest.put(`role/add/${r}/${this.user.id}`, null));
			} else {
				roleRequests.push(this.rest.delete(`role/remove/${r}/${this.user.id}`));
			}
		});
		this.initRoles = JSON.parse(JSON.stringify(this.roles));
		forkJoin(roleRequests).subscribe();
	}
}
